// components/shared/banque/VerifyKeyModal.tsx
import React, { useState } from "react";
import { X, RefreshCw, Key, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { BankTransfertItem } from "../../../../types/features/administration/VirementBancaire";
import BaseModal from "../../../ui/Modal";


interface VerifyKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  transfertItem: BankTransfertItem;
  onVerify: (
    item: BankTransfertItem,
    securityKey: string,
    accountNumber?: string,
    phoneNumber?: string
  ) => Promise<void>;
}

export default function VerifyKeyModal({ isOpen, onClose, transfertItem, onVerify }: VerifyKeyModalProps) {
  const [securityKey, setSecurityKey] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  if (!isOpen) return null;

  const { transfert, details } = transfertItem;

  const handleSubmit = async () => {
    if (!securityKey.trim()) {
      alert("Veuillez entrer la clé de sécurité");
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      await onVerify(transfertItem, securityKey, accountNumber || undefined, phoneNumber || undefined);
      setResult({ success: true, message: "Clé vérifiée avec succès !" });
      setTimeout(() => {
        onClose();
        setSecurityKey("");
        setAccountNumber("");
        setPhoneNumber("");
        setResult(null);
      }, 1500);
    } catch (error: any) {
      setResult({ success: false, message: error.message || "Échec de la vérification" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Vérifier la clé de sécurité" >
      <div className="w-full  bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-t-2xl">
          <h2 className="flex items-center gap-2 text-xl font-bold text-white">
            <Key className="w-5 h-5" />
            Vérifier la clé de sécurité
          </h2>
          <button onClick={onClose} className="p-1 text-white rounded-lg hover:bg-indigo-800">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Infos transfert */}
          <div className="p-3 bg-gray-100 rounded-lg">
            <p className="text-xs text-gray-500">Référence</p>
            <p className="font-mono font-bold text-sm">{transfert.txt_reference}</p>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-600">Bénéficiaire</p>
            <p className="text-sm font-semibold">{details.bank_account_owner}</p>
            <p className="text-xs text-gray-500">{details.bank_name}</p>
            <p className="text-xs font-mono text-gray-500 mt-1">{details.account_number}</p>
          </div>

          {result ? (
            <div
              className={`p-4 rounded-xl border-2 ${result.success ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}
            >
              <div className="flex items-center gap-2">
                {result.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                <p className={`font-semibold ${result.success ? "text-green-700" : "text-red-700"}`}>
                  {result.message}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Champ clé de sécurité */}
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700">
                  Clé de sécurité <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={securityKey}
                  onChange={(e) => setSecurityKey(e.target.value.toUpperCase())}
                  className="w-full p-3 font-mono text-lg tracking-wider border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: ABC123"
                  maxLength={20}
                />
                <p className="mt-1 text-xs text-gray-400">Clé fournie au bénéficiaire</p>
              </div>

              {/* Numéro de compte (optionnel) */}
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700">
                  Numéro de compte <span className="text-gray-400 text-xs">(optionnel)</span>
                </label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  className="w-full p-3 font-mono border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Numéro de compte bancaire"
                />
              </div>

              {/* Numéro de téléphone (optionnel) */}
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-700">
                  Numéro de téléphone <span className="text-gray-400 text-xs">(optionnel)</span>
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="w-full p-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="+237 6XX XXX XXX"
                />
              </div>

              {/* Avertissement */}
              <div className="flex gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                <p className="text-xs text-yellow-800">
                  La clé de sécurité a été communiquée au bénéficiaire lors de la création du transfert.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl border-t">
          {!result ? (
            <>
              <button
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2 font-semibold text-gray-700 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !securityKey.trim()}
                className="flex items-center justify-center flex-1 gap-2 px-4 py-2 font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Vérifier
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                onClose();
                setSecurityKey("");
                setAccountNumber("");
                setPhoneNumber("");
                setResult(null);
              }}
              className="flex-1 px-4 py-2 font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
            >
              Fermer
            </button>
          )}
        </div>
      </div>
    </BaseModal>
  );
}
